import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Alert, Badge, Button, DataTable, SectionCard, type ColumnDef } from "@/deps/ui-library";
import { useExportYearWorkpaper, useYearEndPreview, useYearWorkpaper } from "@/api/queries";
import type { YearWorkpaper } from "@/api/types";
import { useCan } from "@/app/session";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

type Adjustment = YearWorkpaper["adjustments"][number];
type Balance = YearWorkpaper["trialBalance"][number];

const STATUS: Record<
  Adjustment["status"],
  { label: string; scheme: "default" | "success" | "info" }
> = {
  draft: { label: "Draft", scheme: "info" },
  posted: { label: "Posted", scheme: "success" },
  discarded: { label: "Discarded", scheme: "default" },
};

const NUMERIC = { textAlign: "right" as const, cellClassName: "num" };

/** A signed balance as a bookkeeper reads it: a debit or a credit. */
const side = (amount: string | number) => {
  const figure = Number(amount);
  if (figure === 0) return "–";
  return `${formatAmount(Math.abs(figure))} ${figure > 0 ? "Dr" : "Cr"}`;
};

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <dl className="flex items-baseline justify-between gap-3 text-sm">
      <dt className={strong ? "text-fg font-medium" : "text-fg-muted"}>{label}</dt>
      <dd className={`num text-fg ${strong ? "font-medium" : ""}`}>{formatAmount(value)}</dd>
    </dl>
  );
}

/** Review adjustments: the journals flagged as adjusting, and what they do to profit. */
export function YearAdjustmentsTab({ fiscalYearId }: { fiscalYearId: string }) {
  const navigate = useNavigate();
  const can = useCan();
  const { data, isPending, error, refetch } = useYearWorkpaper(fiscalYearId);

  const columns = useMemo<ColumnDef<Adjustment>[]>(
    () => [
      {
        header: "Journal",
        id: "journal",
        cell: ({ row }) => (
          <Link
            to="/accounting/journals/$id"
            params={{ id: row.original.id }}
            className="text-primary underline"
          >
            {row.original.number ?? "Draft"}
          </Link>
        ),
      },
      {
        header: "Accounting date",
        id: "date",
        cell: ({ row }) => formatDate(row.original.date),
      },
      { header: "Narration", accessorKey: "narration" },
      {
        header: "Status",
        id: "status",
        cell: ({ row }) => (
          <Badge variant="soft" size="sm" colorScheme={STATUS[row.original.status].scheme}>
            {STATUS[row.original.status].label}
          </Badge>
        ),
      },
      {
        header: "Reverses next year",
        id: "reverses",
        cell: ({ row }) => (row.original.reversesNextYear ? "Yes" : "No"),
      },
      {
        header: "Effect on profit",
        id: "effect",
        meta: NUMERIC,
        cell: ({ row }) => formatAmount(row.original.profitEffect),
      },
    ],
    [],
  );

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2 pt-4">
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
        <Button colorScheme="default" variant="outline" size="sm" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (isPending || !data) return <p className="text-fg-muted pt-4 text-sm">Loading adjustments…</p>;

  return (
    <div className="flex flex-col gap-4 pt-4">
      <SectionCard title="Effect on profit">
        <div className="flex max-w-md flex-col gap-2">
          <Figure label="Posted profit" value={data.postedProfit} />
          <Figure label="Drafts to resolve" value={data.draftsEffect} />
          <Figure label="Profit if every draft posts" value={data.profitIfDraftsPost} strong />
        </div>
      </SectionCard>

      <SectionCard
        title="Adjusting journals"
        flush
        actions={
          can("accounting.journals.create") ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon="Plus"
              onClick={() =>
                void navigate({
                  to: "/accounting/journals/new",
                  search: { date: data.endsOn, adjusting: true },
                })
              }
            >
              New adjusting journal
            </Button>
          ) : undefined
        }
      >
        {data.adjustments.length === 0 ? (
          <p className="text-fg-muted p-4 text-sm">
            No journal dated in this year is flagged as adjusting.
          </p>
        ) : (
          <DataTable
            aria-label="Adjusting journals"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={data.adjustments}
          />
        )}
      </SectionCard>
    </div>
  );
}

/** Preview close: what would be carried, and the adjusted trial balance. It posts nothing. */
export function YearPreviewTab({ fiscalYearId }: { fiscalYearId: string }) {
  const { data, isPending, error, refetch } = useYearWorkpaper(fiscalYearId);
  const { data: preview } = useYearEndPreview(fiscalYearId);
  const exporting = useExportYearWorkpaper();
  // The revision this person has read. A posting after that means the figures below have moved under them.
  const [pinned, setPinned] = useState<{ year: string; revision: number } | null>(null);
  if (data && pinned?.year !== fiscalYearId)
    setPinned({ year: fiscalYearId, revision: data.revision });
  const stale = data !== undefined && pinned !== null && pinned.revision !== data.revision;

  const columns = useMemo<ColumnDef<Balance>[]>(
    () => [
      { header: "Code", accessorKey: "code" },
      { header: "Account", accessorKey: "name" },
      {
        header: "Unadjusted",
        id: "unadjusted",
        meta: NUMERIC,
        cell: ({ row }) => side(row.original.unadjusted),
      },
      {
        header: "Adjustments",
        id: "adjustments",
        meta: NUMERIC,
        cell: ({ row }) => side(row.original.adjustments),
      },
      {
        header: "Adjusted",
        id: "adjusted",
        meta: NUMERIC,
        cell: ({ row }) => side(row.original.adjusted),
      },
    ],
    [],
  );

  const download = () =>
    exporting.mutate(fiscalYearId, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Year-end-workpaper-${data?.fiscalYearCode ?? ""}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
      },
    });

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2 pt-4">
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
        <Button colorScheme="default" variant="outline" size="sm" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (isPending || !data) return <p className="text-fg-muted pt-4 text-sm">Loading the preview…</p>;

  return (
    <div className="flex flex-col gap-4 pt-4">
      {stale ? (
        <div className="flex flex-col items-start gap-2">
          <Alert
            config={{ variant: "subtle", colorScheme: "warning" }}
            description="Something has posted since you opened this preview, so these figures are newer than the ones you read."
          />
          <Button
            colorScheme="default"
            variant="outline"
            size="sm"
            onClick={() => setPinned({ year: fiscalYearId, revision: data.revision })}
          >
            I've checked the new figures
          </Button>
        </div>
      ) : null}
      {exporting.error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(exporting.error as Error).message}
        />
      ) : null}

      <SectionCard title="Closing journal">
        <div className="flex max-w-md flex-col gap-2 text-sm">
          <Figure
            label="The year's result, going to retained earnings"
            value={data.postedProfit}
            strong
          />
          <p className="text-fg-muted">
            {preview?.method === "manual"
              ? "Not posted by Rigo. You choose the closing journal when you close the year."
              : "Calculated. Rigo posts it when you close the year, and nothing has posted yet."}
          </p>
          {Number(data.draftsEffect) !== 0 ? (
            <p className="text-fg-muted">
              Adjusting drafts aren't in these figures. They'd change the result by{" "}
              <span className="num">{formatAmount(data.draftsEffect)}</span>.
            </p>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard
        title={`Adjusted trial balance · ${formatDate(data.endsOn)}`}
        flush
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon="Download"
            disabled={exporting.isPending}
            onClick={download}
          >
            {exporting.isPending ? "Exporting" : "Export workpaper"}
          </Button>
        }
      >
        {data.trialBalance.length === 0 ? (
          <p className="text-fg-muted p-4 text-sm">Nothing has posted in this year.</p>
        ) : (
          <DataTable
            aria-label="Adjusted trial balance"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={data.trialBalance}
          />
        )}
      </SectionCard>
    </div>
  );
}
